import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeSubmit, OnBlur, OnChange, OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ActionAPI, AutocompleteAPI, CheckboxAPI, FormAPI, InputAPI, ModalProps, ModalService, RadioGroupAPI, SelectableItem, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Inject } from '@odinjs/odin';
import { generateUUID } from '@philips-internal/oswald-ui-utils';

import moment from 'moment';
import { WoundsEditAliases as Alias, FragmentNames } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { Endpoint } from '../../../../constants/endpoints';
import { WizardSteps } from '../../../../constants/wizards';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { FULL_LOCALE_DATE, ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { WoundsRadioGroupId } from '../wounds-radio-group-data/wounds-radio-group-expressions';
import { DomainType, SearchType, WoundsDressingTabType, WoundsResponse } from '../../types/wounds-dressings-types';
import DialogService from '../../../../services/dialog-service';
import { ApplicationRoute } from '../../../../constants/routes';
import { WoundsDressingsTabListContext } from '../../service/wounds-dressing-tab-list-service';
import { WoundsDressingContext } from '../../wounds-dressing-service';

@Controller({ component: Alias.LAYOUT_WOUNDS_EDIT, fragment: FragmentNames.WOUNDS_EDIT })
export default class WoundsEditController extends BaseController {

  @Inject(Alias.INPUT_START_DATE)
  private inputStartDateAPI!: InputAPI;

  @Inject(Alias.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(Alias.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(Alias.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(Alias.FORM_FOUR)
  private formFourAPI!: FormAPI;

  @Inject(Alias.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(Alias.AUTOCOMPLETE_COMPLEMENT)
  private autocompleteComplementAPI!: AutocompleteAPI;

  @Inject(Alias.AUTOCOMPLETE_WOUND_CLASSIFCATION)
  private autocompleteClassificationAPI!: AutocompleteAPI;

  @Inject(Alias.ACTION_DELETE_WOUND)
  private actionDeleteAPI!: ActionAPI;

  @Inject(Alias.ACTION_NEW_WOUND)
  private actionNewAPI!: ActionAPI;

  @Inject(Alias.ACTION_RELEASE_WOUND)
  private actionReleaseAPI!: ActionAPI;

  @Inject(Alias.ACTION_END_TREATMENT)
  private actionEndTreatmentAPI!: ActionAPI;

  @Inject(Alias.ACTION_UNDO_END_TREATMENT)
  private actionUndoEndAPI!: ActionAPI;

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'wounds-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private setRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR)
      });
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    this.wizardAPI.setStepDone(step, isStepDone);
  }

  private handleComplement(): void {
    this.autocompleteComplementAPI.setEnabled(false);
    this.autocompleteComplementAPI.setValue(undefined);
    const location = this.context.get('locationCode') as number;

    if (location) {
      this.autocompleteComplementAPI.setEnabled(true);
    }
  }

  private handleClassification(): void {
    this.autocompleteClassificationAPI.setEnabled(false);
    this.autocompleteClassificationAPI.setValue(undefined);
    const woundTypeCode = this.context.get('woundTypeCode') as SearchType;

    if (woundTypeCode) {
      this.autocompleteClassificationAPI.setEnabled(true);
    }
  }

  private handleReleaseWound(woundId: number): void {
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);
    this.formFourAPI.setLoading(true);

    HttpService.put(Endpoint.RELEASE_WOUND, { sequenceNumberWound: woundId })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
        this.formTwoAPI.setLoading(false);
        this.formThreeAPI.setLoading(false);
        this.formFourAPI.setLoading(false);
      })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private showModalSubmit(woundId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.handleReleaseWound(woundId) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    })
  }

  private handleCreateWound(body: Record<string, Value>): void {
    this.formFourAPI.setLoading(true);

    HttpService.post(Endpoint.POST_WOUND, undefined, {body})
      .then((res) => {
        const { woundId } = res.body as WoundsResponse;
        this.showModalSubmit(woundId as number);
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formFourAPI.setLoading(false));
  }

  private displayEndTreatment(woundsRecord: Record<string, Value>): void {
    if (!WoundsDressingContext.getAllowEdit()) {
      this.actionUndoEndAPI.setEnabled(false);

    } else {
      if (woundsRecord.releaseDate && woundsRecord.reasonEndTreatment) {
        this.actionUndoEndAPI.setEnabled(true);
        return;
      }
      this.actionEndTreatmentAPI.setEnabled(true);
      return;
    }
  }

  private handleFormValidation(woundsRecord: Record<string, Value>): void {
    this.formOneAPI.setEditing(true);
    this.formOneAPI.setDisabled(true);

    this.actionDeleteAPI.setEnabled(WoundsDressingContext.getAllowEdit());
    this.actionNewAPI.setEnabled(WoundsDressingContext.getAllowEdit());
    this.actionReleaseAPI.setEnabled(WoundsDressingContext.getAllowEdit());
    this.displayEndTreatment(woundsRecord);

    this.formOneAPI.setData(woundsRecord as unknown as Record<string, WoundsResponse>);
    this.formTwoAPI.setData(woundsRecord as unknown as Record<string, WoundsResponse>);
    this.formThreeAPI.setData(woundsRecord as unknown as Record<string, WoundsResponse>);
    this.formFourAPI.setData(woundsRecord as unknown as Record<string, WoundsResponse>);

    this.wizardAPI.setStepDone(WizardSteps.STEP_ONE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_FOUR, true);
  }

  private handleTreatData(record: WoundsResponse): void {
    const unknownStart            = BooleanUtils.isTrueValue(record.unknownStart);
    const reportedPain            = BooleanUtils.isTrueValue(record.reportedPain);
    const medicineAllergy         = BooleanUtils.isTrueValue(record.medicineAllergy);
    const previousHistory         = BooleanUtils.isTrueValue(record.previousHistory);

    const startDate               = record.startDate ? record.startDate : '';
    const releaseDate             = record.releaseDate ? moment(record.releaseDate).local().format(FULL_LOCALE_DATE) : undefined

    const radioData               = record.admittedInjury;
    const admittedInjury          = { key: radioData, label: '', value: radioData };

    const woundLocation           = record.location;
    const mainDiagnosis           = record.diagnosis;
    const woundClassification     = record.classification;

    const location                = record.location?.id;
    const woundShape              = record.woundShape?.valueDomain;
    const woundTypeCode           = record.woundType?.id

    const woundsRecord = {
      ...record,
      admittedInjury,
      mainDiagnosis,
      medicineAllergy,
      previousHistory,
      releaseDate,
      reportedPain,
      startDate,
      unknownStart,
      woundClassification,
      woundLocation,
    };

    this.context.set({
      locationCode: woundLocation?.id,
      wounds: woundsRecord,
      woundTypeCode,
    });

    if (location) {
      this.autocompleteComplementAPI.setEnabled(true);
    }

    if (woundShape) {
      this.autocompleteClassificationAPI.setEnabled(true);
    }

    if (unknownStart) {
      this.inputStartDateAPI.setEnabled(false);
    }

    this.handleFormValidation(woundsRecord);
  }

  private delete(woundId: number): void {
    const sequenceNumberWound = woundId;
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);
    this.formFourAPI.setLoading(true);
    HttpService.delete(Endpoint.WOUND, {
      sequenceNumberWound
    }).then(() => {
      applicationContext.popNavigationItem();
    }).catch((err) => {
      err?.body && this.showToast(err?.body, ToastLevel.ERROR);
    }).finally(() => {
      this.formOneAPI.setLoading(false);
      this.formTwoAPI.setLoading(false);
      this.formThreeAPI.setLoading(false);
      this.formFourAPI.setLoading(false);
    })
  }

  private confirmDelete(woundId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(woundId)},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private handleFetchWounds(): Promise<void> {
    const woundId = this.context.get('woundId');
    return HttpService.get(Endpoint.WOUND, {
      sequenceNumberWound: woundId
    })
      .then(response => {
        const res = response.body as WoundsResponse;
        this.handleTreatData(res);
      })
      .catch((error) => this.showToast(error?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formOneAPI.setLoading(false);
      });
  }

  private undoEndTreatment(): void {
    const { woundId } = this.historyService.getCurrentRouteParams();
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);
    this.formFourAPI.setLoading(true);

    const body = {
      woundId
    } as Record<string, Value>;

    HttpService.put(Endpoint.UNDO_END_TREATMENT_WOUND, undefined, {body})
      .then(() => {
        applicationContext.popNavigationItem();
        this.showToast('label.operation-done', ToastLevel.SUCCESS);
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
        this.formTwoAPI.setLoading(false);
        this.formThreeAPI.setLoading(false);
        this.formFourAPI.setLoading(false);
      });
  }

  private openModalEndTreatment(): void {
    const { woundId } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'wounds-end-treatment',
      id: 'wounds-end-treatment',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'label.end-treatment',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      woundId
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  @OnMount(Alias.FORM_ONE)
  handleMountFormOne(event: MountEvent): void {
    const api = event.target as FormAPI;
    api.setData({...api.getData(), alert: true });
    this.formOneAPI.setLoading(true);
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
    this.actionEndTreatmentAPI.setEnabled(false);
    this.actionUndoEndAPI.setEnabled(false);
  }

  @OnMount(Alias.WIZARD)
  handleMountWizard(): void {
    const { encounterId, patientId, woundId } = this.historyService.getCurrentRouteParams();
    const establishmentId = foundationContext.getEstablishment()?.id;
    this.context.set({ encounterId, establishmentId, patientId, woundId });
    InformationBarUtils.collapsedBar = true;
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.WOUNDS;
    this.handleFetchWounds();
  }

  @OnMount(Alias.RADIO_GROUP_ADMITTED_INJURY)
  handleMountRadioGroup(event: MountEvent): void {
    const api = event.target as RadioGroupAPI;
    this.setRadioGroupValues(WoundsRadioGroupId.GROUP_ID_ADMITTED_INJURED, api);
  }

  @OnValidate(Alias.FORM_ONE)
  handleValidateFormOne(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(Alias.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(Alias.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnValidate(Alias.FORM_FOUR)
  handleValidateFormFour(validationFormFour: ValidateEvent): void {
    this.handleStepDone(validationFormFour.valid, WizardSteps.STEP_FOUR);
  }

  @OnAfterNavigate(Alias.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_FOUR, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  @OnClick(Alias.ACTION_RELEASE_WOUND)
  handleClickRelease(): void {
    const { woundId } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(woundId as number);
  }

  @OnClick(Alias.ACTION_NEW_WOUND)
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.WOUNDS_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick(Alias.ACTION_DELETE_WOUND)
  handleClickDelete(): void {
    const { woundId } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(woundId as number);
  }

  @OnClick(Alias.ACTION_END_TREATMENT)
  handleClickEndTreatment(): void {
    this.openModalEndTreatment();
  }

  @OnClick(Alias.ACTION_UNDO_END_TREATMENT)
  handleClickUndoEndTreatment(): void {
    this.undoEndTreatment();
  }

  @OnBlur(Alias.INPUT_START_DATE)
  handleBlurDate(): void {
    const inputDate = this.inputStartDateAPI.getValue() as string;
    const requiredLength = 19;
    if (inputDate.length < requiredLength && inputDate.length !== 0) {
      this.showToast('text.report-date-and-hour', ToastLevel.WARNING);
    }
  }

  @OnChange(Alias.AUTOCOMPLETE_WOUND_LOCATION)
  handleChangeComplement(event: ChangeEvent<SearchType>): void {
    const woundLocation = event.value as unknown as SearchType;
    this.context.set({ locationCode: woundLocation?.id });
    this.handleComplement();
  }

  @OnChange(Alias.AUTOCOMPLETE_WOUND_TYPE)
  handleChangeWoundType(event: ChangeEvent<SearchType>): void {
    const woundTypeCode = event.value as unknown as SearchType;
    this.context.set({ woundTypeCode: woundTypeCode?.id });
    this.handleClassification();
  }

  @OnAfterFetch(Alias.FETCH_COMPLEMENT)
  handleFetchComplement(event: FetchEvent): void {
    const complements = (event.data as Record<string, Value>[]);
    if (!complements.length) {
      this.autocompleteComplementAPI.setEnabled(false);
    }
  }

  @OnAfterFetch(Alias.FETCH_WOUND_CLASSIFICATION)
  handleFetchClassification(event: FetchEvent): void {
    const classifications = (event.data as Record<string, Value>[]);
    if (!classifications.length) {
      this.autocompleteClassificationAPI.setEnabled(false);
    }
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_START)
  handleChangeUnknownStart(event: ChangeEvent<CheckboxAPI>): void {
    const unknownStart = event.value as unknown as boolean;
    this.inputStartDateAPI.setValue('');
    this.inputStartDateAPI.setEnabled(!unknownStart);
  }

  @OnBeforeSubmit(Alias.WIZARD)
  handleBeforeSubmit(): void {
    const { encounterId, patientId, woundId } = this.historyService.getCurrentRouteParams();
    const user                       = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const establishmentId = foundationContext.getEstablishment()?.id;
    const formDataOne                = this.formOneAPI.getRecord();
    const formDataTwo                = this.formTwoAPI.getRecord();
    const formDataThree              = this.formThreeAPI.getRecord();
    const formDataFour               = this.formFourAPI.getRecord();
    const clinic                     = (formDataOne.clinic as SearchType)?.id || (formDataOne.clinic as DomainType)?.valueDomain;

    const body = {
      admittedInjury: (formDataTwo.admittedInjury as SelectableItem)?.key,
      allergies: formDataThree.allergies as string,
      classification: (formDataTwo.woundClassification as SearchType)?.id,
      clinic,
      complement: (formDataTwo.complement as SearchType)?.id,
      diagnosis: formDataOne.mainDiagnosis as string,
      establishmentCode: establishmentId as unknown as number,
      location: (formDataOne.woundLocation as SearchType)?.id,
      medicineAllergy: BooleanUtils.isPositiveValue(formDataThree.medicineAllergy as boolean),
      numberService: parseInt(encounterId as unknown as string),
      observation: formDataThree.observation as string,
      painSite: formDataThree.painSite as string,
      physicalPerson: patientId,
      previousHistory: BooleanUtils.isPositiveValue(formDataFour.previousHistory as boolean),
      professional: user.physicalPersonId,
      reportedPain: BooleanUtils.isPositiveValue(formDataThree.reportedPain as boolean),
      sector: (formDataOne.sector as SearchType)?.id,
      startDate: formDataOne.startDate ? moment(String(formDataOne.startDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      unknownStart: BooleanUtils.isPositiveValue(formDataOne.unknownStart as boolean),
      whatUsed: formDataFour.whatUsed as string,
      whoTreated: formDataFour.whoTreated as string,
      woundDay: formDataFour.woundDay as number,
      woundId: woundId as number,
      woundMonth: formDataFour.woundMonth as number,
      woundShape: (formDataTwo.woundShape as DomainType)?.valueDomain,
      woundType: (formDataTwo.woundType as SearchType)?.id,
      woundYear: formDataFour.woundYear as number,
    } as Record<string, Value>;

    this.handleCreateWound(body);
  }

}
